// Pure metric derivation for the judgment lenses (`openFindings` / `findingsTopicMetric`).
// Building-scope Havainnot count in `LensTopicSection` uses `openFindings`; findings already
// loaded for the scope — no extra API. The portfolio-scope ranking this module used to also
// feed (`rankBuildingsByOpenFindings`) was retired with `LensPortfolioRollup` (#1296 task
// #1308): the judgment lenses now render the shared overview grammar, whose worst-first tile
// grid is that ranking.
import type { Finding } from "@/lib/api/building";
import type { CardTone } from "@/lib/card-styles";

export type LensFindingFilter = {
  category?: Finding["category"];
  sourceType?: Finding["source_type"];
};

export const DATA_QUALITY_FINDINGS_FILTER: LensFindingFilter = {
  category: "data_quality",
};

export const FAULT_FINDINGS_FILTER: LensFindingFilter = {
  category: "fault",
};

export const INDOOR_CONDITIONS_FINDINGS_FILTER: LensFindingFilter = {
  sourceType: "zone",
};

export const OPPORTUNITY_FINDINGS_FILTER: LensFindingFilter = {
  category: "opportunity",
};

/** Same filter the quiet caption uses — the ribbon dialog joins this page's findings. */
export function cycleSlotLensFromTrack(
  findings: Finding[] | null,
  findingsList: LensFindingFilter,
): { findings: Finding[] | null; filter: LensFindingFilter } {
  return {
    findings,
    filter: {
      category: findingsList.category,
      sourceType: findingsList.sourceType,
    },
  };
}

export type TopicMetric = {
  value: string;
  hint: string;
  tone: CardTone;
};

function matchesFilter(f: Finding, filter: LensFindingFilter): boolean {
  if (filter.category !== undefined && f.category !== filter.category) {
    return false;
  }
  if (filter.sourceType !== undefined && f.source_type !== filter.sourceType) {
    return false;
  }
  return true;
}

export function openFindings(
  findings: Finding[],
  filter: LensFindingFilter,
): Finding[] {
  return findings.filter(
    (f) => f.status === "open" && matchesFilter(f, filter),
  );
}

export function findingsTopicMetric(
  findings: Finding[] | null,
  filter: LensFindingFilter,
): TopicMetric {
  if (findings === null) {
    return { value: "…", hint: "ladataan", tone: "empty" };
  }
  const open = openFindings(findings, filter);
  if (open.length === 0) {
    return { value: "0", hint: "ei avoimia havaintoja", tone: "ok" };
  }
  const critical = open.filter((f) => f.severity === "critical").length;
  return {
    value: String(open.length),
    hint: critical > 0 ? `joista ${critical} kriittistä` : "ei kriittisiä",
    tone: "warn",
  };
}

export function summaryTopicMetric(
  findings: Finding[] | null,
  filter: LensFindingFilter,
  opts?: {
    zoneCount?: number | null;
    /** Override default zone-oriented hints (e.g. data-quality lens). */
    okHint?: string;
    openHint?: string;
  },
): TopicMetric {
  if (findings === null) {
    return { value: "…", hint: "ladataan", tone: "empty" };
  }
  const open = openFindings(findings, filter);
  const zoneCount = opts?.zoneCount;
  const okHint = opts?.okHint ?? "ei avoimia vyöhykepoikkeamia";
  const openHint = opts?.openHint ?? "avoimia vyöhykepoikkeamia";
  if (zoneCount != null && zoneCount > 0) {
    const clean = Math.max(0, zoneCount - open.length);
    // Not a perfect zone-health %, but honest: open zone findings vs known zones.
    const pct = Math.round((clean / zoneCount) * 100);
    return {
      value: `${pct} %`,
      hint:
        open.length === 0
          ? `${zoneCount} vyöhykettä · ei avoimia poikkeamia`
          : `${open.length} poikkeamaa · ${zoneCount} vyöhykettä`,
      tone: open.length === 0 ? "ok" : "warn",
    };
  }
  if (open.length === 0) {
    return { value: "OK", hint: okHint, tone: "ok" };
  }
  return {
    value: String(open.length),
    hint: openHint,
    tone: "warn",
  };
}

export function activityTopicMetric(
  findings: Finding[] | null,
  buildingCount: number,
): TopicMetric {
  if (findings === null) {
    return { value: "…", hint: "ladataan", tone: "empty" };
  }
  if (buildingCount <= 0) {
    return { value: "—", hint: "ei kohteita rajauksessa", tone: "empty" };
  }
  if (buildingCount === 1) {
    return { value: "—", hint: "kohteen tapahtumaloki", tone: "empty" };
  }
  return {
    value: String(buildingCount),
    hint: "kohdetta · tapahtumaloki",
    tone: "empty",
  };
}
